import { useState, useEffect, useCallback } from "react";
import {
  X, Search, Check, ChevronDown, ChevronUp, Save, RefreshCw,
  User, FileText, Package, Calculator, Minus, Plus, Building2, ShowerHead, DoorOpen, Bath,
  Sparkles, Loader2, PlusCircle
} from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const formatPrice = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);

export default function DevisCreationForm({ type = "mpa", onClose, preSelectedClient }) {
  const [step, setStep] = useState(preSelectedClient ? 2 : 1);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(preSelectedClient || null);
  const [catalogue, setCatalogue] = useState([]);
  const [societe, setSociete] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [amoMarList, setAmoMarList] = useState([]);
  const [selectedAmo, setSelectedAmo] = useState(null);
  const [selectedMar, setSelectedMar] = useState(null);
  const [receveurs, setReceveurs] = useState([]);
  const [selectedReceveur, setSelectedReceveur] = useState(null);
  const [parois, setParois] = useState([]);
  const [selectedParoi, setSelectedParoi] = useState(null);
  const [meubles, setMeubles] = useState([]);
  const [selectedMeuble, setSelectedMeuble] = useState(null);

  // Revetement surface fields
  const [surfaceMur, setSurfaceMur] = useState(0);
  const [surfaceSol, setSurfaceSol] = useState(0);
  const [m2ParBoiteMur, setM2ParBoiteMur] = useState(1.95);
  const [m2ParBoiteSol, setM2ParBoiteSol] = useState(1.95);

  // Custom prestation via IA
  const [customDesc, setCustomDesc] = useState('');
  const [customLoading, setCustomLoading] = useState(false);
  const [customResult, setCustomResult] = useState(null);
  const [customPrestations, setCustomPrestations] = useState([]);
  const [savedPrestations, setSavedPrestations] = useState([]);
  const [saveToDb, setSaveToDb] = useState(true);
  const [devisInfo, setDevisInfo] = useState({
    estimation_mpa: 0,
    notes: "",
    condition_gir: "Sans Condition de GIR",
  });

  // Load societe + catalogue + AMO/MAR + receveurs
  useEffect(() => {
    (async () => {
      try {
        const [sr, amr] = await Promise.all([
          authFetch(`${API}/societes`),
          authFetch(`${API}/amo-mar`),
        ]);
        const societes = await sr.json();
        if (societes.length > 0) {
          setSociete(societes[0]);
          const [cr, rr, pr, mr] = await Promise.all([
            authFetch(`${API}/societes/${societes[0].id}/catalogue`),
            authFetch(`${API}/societes/${societes[0].id}/catalogue/receveurs`),
            authFetch(`${API}/societes/${societes[0].id}/catalogue/parois`),
            authFetch(`${API}/societes/${societes[0].id}/catalogue/meubles`),
          ]);
          const cdata = await cr.json();
          const items = cdata.items || [];
          setCatalogue(items);
          const exp = {};
          const preSelected = {};
          items.filter(i => i.type_devis === type).forEach(i => {
            exp[i.section] = true;
            preSelected[i.id] = { qty: 1, prix_ht: i.prix_ht };
          });
          setSelectedItems(preSelected);
          setExpandedSections(exp);
          if (rr.ok) {
            const rdata = await rr.json();
            setReceveurs(rdata.receveurs || []);
          }
          if (pr.ok) {
            const pdata = await pr.json();
            setParois(pdata.parois || []);
          }
          if (mr.ok) {
            const mdata = await mr.json();
            setMeubles(mdata.meubles || []);
          }
        }
        if (amr.ok) {
          const amrData = await amr.json();
          setAmoMarList(amrData.entries || []);
        }
      } catch (e) { console.error(e); }
      // Fetch saved custom prestations
      try {
        const cpr = await authFetch(`${API}/devis/custom-prestations`);
        if (cpr.ok) {
          const cpdata = await cpr.json();
          setSavedPrestations(cpdata.prestations || []);
        }
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Search clients
  const searchClients = useCallback(async (q) => {
    if (q.length < 2) { setClients([]); return; }
    try {
      const r = await authFetch(`${API}/devis-clients/search?q=${encodeURIComponent(q)}&limit=20`);
      if (r.ok) setClients(await r.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchClients(clientSearch), 300);
    return () => clearTimeout(t);
  }, [clientSearch, searchClients]);

  const toggleItem = (itemId) => {
    setSelectedItems(prev => {
      const copy = { ...prev };
      if (copy[itemId]) {
        delete copy[itemId];
      } else {
        const item = catalogue.find(i => i.id === itemId);
        const prix = (itemId === genericReceveurId && selectedReceveur) ? selectedReceveur.prix_ht
          : (itemId === genericParoiId && selectedParoi) ? selectedParoi.prix_ht
          : (itemId === genericMeubleId && selectedMeuble) ? selectedMeuble.prix_ht
          : item.prix_ht;
        copy[itemId] = { qty: 1, prix_ht: prix };
      }
      return copy;
    });
    // Uncheck from deletion if toggled off
    setCheckedForDelete(prev => { const c = new Set(prev); c.delete(itemId); return c; });
  };

  // Checkboxes for batch deletion
  const [checkedForDelete, setCheckedForDelete] = useState(new Set());
  const toggleDeleteCheck = (itemId) => {
    setCheckedForDelete(prev => {
      const c = new Set(prev);
      if (c.has(itemId)) c.delete(itemId); else c.add(itemId);
      return c;
    });
  };
  const deleteChecked = () => {
    setSelectedItems(prev => {
      const copy = { ...prev };
      checkedForDelete.forEach(id => delete copy[id]);
      return copy;
    });
    setCheckedForDelete(new Set());
  };

  const updateItemQty = (itemId, qty) => {
    if (qty < 1) return;
    setSelectedItems(prev => ({ ...prev, [itemId]: { ...prev[itemId], qty } }));
  };

  const updateItemPrice = (itemId, prix_ht) => {
    setSelectedItems(prev => ({ ...prev, [itemId]: { ...prev[itemId], prix_ht } }));
  };

  // IA reformulation
  const handleReformulate = async () => {
    if (!customDesc.trim()) return;
    setCustomLoading(true);
    setCustomResult(null);
    try {
      const r = await authFetch(`${API}/devis/reformulate-prestation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: customDesc })
      });
      if (r.ok) {
        const data = await r.json();
        if (data.success) {
          setCustomResult(data);
        } else {
          setCustomResult({ ...data, error: true });
        }
      }
    } catch (e) { console.error(e); }
    setCustomLoading(false);
  };

  const addCustomPrestation = async () => {
    if (!customResult) return;
    const id = `custom-${Date.now()}`;
    const prestation = {
      id,
      nom: customResult.nom,
      description: customResult.description,
      prix_ht: customResult.prix_ht,
      unite: customResult.unite,
      justification_prix: customResult.justification_prix,
      auteur: customResult.auteur,
    };
    setCustomPrestations(prev => [...prev, prestation]);
    setSelectedItems(prev => ({ ...prev, [id]: { qty: 1, prix_ht: prestation.prix_ht } }));

    // Save to DB if requested
    if (saveToDb) {
      try {
        const r = await authFetch(`${API}/devis/custom-prestations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(prestation)
        });
        if (r.ok) {
          const data = await r.json();
          setSavedPrestations(prev => [data.prestation, ...prev]);
        }
      } catch (e) { console.error(e); }
    }

    setCustomDesc('');
    setCustomResult(null);
    setSaveToDb(true);
  };

  const addSavedPrestation = (savedId) => {
    const saved = savedPrestations.find(p => p.id === savedId);
    if (!saved) return;
    const id = `saved-${Date.now()}`;
    const prestation = { ...saved, id };
    setCustomPrestations(prev => [...prev, prestation]);
    setSelectedItems(prev => ({ ...prev, [id]: { qty: 1, prix_ht: saved.prix_ht } }));
  };

  // ID of the generic "Receveur de douche" and "Paroi de douche" catalogue items
  const genericReceveurItem = catalogue.find(i => i.type_devis === type && i.nom?.toLowerCase() === 'receveur de douche');
  const genericReceveurId = genericReceveurItem?.id;
  const genericParoiItem = catalogue.find(i => i.type_devis === type && i.nom?.toLowerCase() === 'paroi de douche');
  const genericParoiId = genericParoiItem?.id;
  const genericMeubleItem = catalogue.find(i => i.type_devis === type && i.nom?.toLowerCase() === 'ensemble meuble lavabo');
  const genericMeubleId = genericMeubleItem?.id;

  // Revetement IDs
  const panneauxMurauxId = catalogue.find(i => i.type_devis === type && i.nom?.toLowerCase() === 'panneaux muraux pvc')?.id;
  const dallesSolId = catalogue.find(i => i.type_devis === type && i.nom?.toLowerCase() === 'dalles sols antiderapant')?.id;
  const poseMuralId = catalogue.find(i => i.type_devis === type && i.nom?.toLowerCase() === 'pose revetement mural')?.id;
  const poseSolId = catalogue.find(i => i.type_devis === type && i.nom?.toLowerCase() === 'pose revetement sols')?.id;
  const isRevetementItem = (id) => id === panneauxMurauxId || id === dallesSolId || id === poseMuralId || id === poseSolId;

  // Auto-update revetement quantities when surface changes
  useEffect(() => {
    if (!panneauxMurauxId && !dallesSolId) return;
    setSelectedItems(prev => {
      const copy = { ...prev };
      if (panneauxMurauxId && copy[panneauxMurauxId] && surfaceMur > 0) {
        copy[panneauxMurauxId] = { ...copy[panneauxMurauxId], qty: Math.ceil(surfaceMur * 1.15 / m2ParBoiteMur) };
      }
      if (dallesSolId && copy[dallesSolId] && surfaceSol > 0) {
        copy[dallesSolId] = { ...copy[dallesSolId], qty: Math.ceil(surfaceSol * 1.15 / m2ParBoiteSol) };
      }
      if (poseMuralId && copy[poseMuralId] && surfaceMur > 0) {
        copy[poseMuralId] = { ...copy[poseMuralId], qty: surfaceMur };
      }
      if (poseSolId && copy[poseSolId] && surfaceSol > 0) {
        copy[poseSolId] = { ...copy[poseSolId], qty: surfaceSol };
      }
      return copy;
    });
  }, [surfaceMur, surfaceSol, m2ParBoiteMur, m2ParBoiteSol, panneauxMurauxId, dallesSolId, poseMuralId, poseSolId]);

  // Grouped catalogue - keep all items including generic receveur/paroi
  const grouped = {};
  catalogue.filter(i => i.type_devis === type).forEach(i => {
    if (!grouped[i.section]) grouped[i.section] = [];
    grouped[i.section].push(i);
  });

  // When a specific receveur/paroi is selected, auto-update the generic item's display
  const getDisplayItem = (item) => {
    if (item.id === genericReceveurId && selectedReceveur) {
      return { ...item, nom: selectedReceveur.nom, marque: selectedReceveur.marque, prix_ht: selectedReceveur.prix_ht };
    }
    if (item.id === genericParoiId && selectedParoi) {
      return { ...item, nom: selectedParoi.nom, marque: selectedParoi.marque, prix_ht: selectedParoi.prix_ht };
    }
    if (item.id === genericMeubleId && selectedMeuble) {
      return { ...item, nom: selectedMeuble.nom, marque: selectedMeuble.marque, prix_ht: selectedMeuble.prix_ht };
    }
    return item;
  };

  // Is this item a replaced generic?
  const isReplacedItem = (id) => (id === genericReceveurId && selectedReceveur) || (id === genericParoiId && selectedParoi) || (id === genericMeubleId && selectedMeuble);
  const getReplacementFor = (id) => {
    if (id === genericReceveurId && selectedReceveur) return selectedReceveur;
    if (id === genericParoiId && selectedParoi) return selectedParoi;
    if (id === genericMeubleId && selectedMeuble) return selectedMeuble;
    return null;
  };

  // Totals - use replaced receveur/paroi price if selected
  const totalHT = Object.entries(selectedItems).reduce((sum, [id, { qty, prix_ht }]) => {
    const replacement = getReplacementFor(id);
    if (replacement) return sum + qty * replacement.prix_ht;
    return sum + qty * prix_ht;
  }, 0);
  const tvaRate = societe?.tva_rate || 10;
  const totalTVA = totalHT * tvaRate / 100;
  const totalTTC = totalHT + totalTVA;
  const resteAPayer = totalTTC - (devisInfo.estimation_mpa || 0);
  const nbSelected = Object.keys(selectedItems).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      const lines = Object.entries(selectedItems).map(([id, { qty, prix_ht }]) => {
        // Check if it's a custom prestation
        const customP = customPrestations.find(p => p.id === id);
        if (customP) {
          return {
            catalogue_item_id: id,
            nom: customP.nom,
            description: `${customP.description} | ${customP.justification_prix} | Reformulé par: ${customP.auteur}`,
            marque: '',
            section: 'Autres prestations',
            unite: customP.unite,
            quantite: qty,
            prix_unitaire_ht: prix_ht,
            tva_rate: 10,
            total_ht: qty * prix_ht,
            total_ttc: qty * prix_ht * 1.10,
            is_custom: true,
          };
        }

        const item = catalogue.find(i => i.id === id);
        if (!item) return null;
        // Replace generic receveur or paroi with specific one
        const replacement = getReplacementFor(id);
        const nomLine = replacement ? replacement.nom : item.nom;
        let descLine = replacement ? (replacement.description || '') : (item.description || '');
        const marqueLine = replacement ? replacement.marque : item.marque;
        const prixLine = replacement ? replacement.prix_ht : prix_ht;

        // Add surface info to revetement items
        if (id === panneauxMurauxId && surfaceMur > 0) {
          descLine += ` | Superficie reelle: ${surfaceMur} m² — Quantite majoree de 15% pour couvrir les pertes de coupe et de pose (${(surfaceMur * 1.15).toFixed(2)} m²), soit ${qty} boite(s) de ${m2ParBoiteMur} m².`;
        }
        if (id === dallesSolId && surfaceSol > 0) {
          descLine += ` | Superficie reelle: ${surfaceSol} m² — Quantite majoree de 15% pour couvrir les pertes de coupe et de pose (${(surfaceSol * 1.15).toFixed(2)} m²), soit ${qty} boite(s) de ${m2ParBoiteSol} m².`;
        }
        if (id === poseMuralId && surfaceMur > 0) {
          descLine += ` | Surface: ${surfaceMur} m²`;
        }
        if (id === poseSolId && surfaceSol > 0) {
          descLine += ` | Surface: ${surfaceSol} m²`;
        }

        return {
          catalogue_item_id: replacement ? replacement.id : id,
          nom: nomLine,
          description: descLine,
          marque: marqueLine,
          section: item.section,
          unite: item.unite,
          quantite: qty,
          prix_unitaire_ht: prixLine,
          tva_rate: item.tva_rate,
          total_ht: qty * prixLine,
          total_ttc: qty * prixLine * (1 + item.tva_rate / 100),
          ...(replacement ? {
            is_replaced: true,
            taille: replacement.taille,
            reference: replacement.reference,
            type_paroi: replacement.type_paroi,
          } : {}),
        };
      }).filter(Boolean);

      const devis = {
        type_devis: type,
        societe_id: societe?.id,
        client_row_number: selectedClient?.row_number,
        client_nom: selectedClient?.nom || selectedClient?.data?.['CLIENT NOM'] || '',
        client_prenom: selectedClient?.prenom || selectedClient?.data?.['CLIENT PRENOM'] || '',
        client_telephone: selectedClient?.telephone || selectedClient?.data?.['CLIENT TELEPHONE'] || '',
        client_email: selectedClient?.email || selectedClient?.data?.['CLIENT EMAIL'] || '',
        client_adresse: selectedClient?.adresse || selectedClient?.data?.['ADRESSE RDV'] || '',
        client_code_postal: selectedClient?.code_postal || selectedClient?.data?.['CODE POSTAL RDV'] || '',
        client_ville: selectedClient?.ville || selectedClient?.data?.['VILLE RDV'] || '',
        amo_id: selectedAmo?.id || null,
        amo_nom: selectedAmo?.nom_societe || '',
        mar_id: selectedMar?.id || null,
        mar_nom: selectedMar?.nom_societe || '',
        lignes: lines,
        total_ht: totalHT,
        total_tva: totalTVA,
        total_ttc: totalTTC,
        estimation_mpa: devisInfo.estimation_mpa || 0,
        reste_a_payer: resteAPayer,
        condition_gir: devisInfo.condition_gir,
        notes: devisInfo.notes,
      };

      const r = await authFetch(`${API}/devis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(devis),
      });
      if (r.ok) {
        const data = await r.json();
        alert(`Devis ${data.numero} cree avec succes !`);
        onClose?.();
      } else {
        const err = await r.json();
        alert(`Erreur: ${err.detail || 'Impossible de creer le devis'}`);
      }
    } catch (e) {
      console.error(e);
      alert("Erreur reseau");
    }
    setSaving(false);
  };

  const typeLabels = { mpa: "MPA - MaPrimeAdapt'", pac_iso: "PAC / ISO", ts: "TS - Travaux Supplementaires" };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[80] flex items-start justify-center overflow-y-auto py-8">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-5xl mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-white">Nouveau devis {typeLabels[type]}</h2>
            <p className="text-sm text-slate-400">{societe ? `${societe.forme_juridique} ${societe.raison_sociale}` : 'Chargement...'}</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Steps */}
            <div className="flex items-center gap-1 text-xs">
              {[
                { n: 1, label: "Client" },
                { n: 2, label: "Prestations" },
                { n: 3, label: "Resume" },
              ].map(s => (
                <div key={s.n} className={`flex items-center gap-1 px-3 py-1.5 rounded-full ${
                  step === s.n ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50' :
                  step > s.n ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-500'
                }`}>
                  {step > s.n ? <Check className="w-3 h-3" /> : <span>{s.n}</span>}
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
          </div>
        </div>

        <div className="p-5">
          {/* STEP 1: Select client */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 bg-slate-800 rounded-lg border border-slate-700 px-3 py-3">
                <Search className="w-5 h-5 text-slate-500" />
                <input
                  data-testid="devis-client-search"
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                  placeholder="Rechercher un client (nom, telephone, email...)"
                  className="bg-transparent w-full text-white outline-none"
                  autoFocus
                />
              </div>
              {clients.length > 0 && (
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {clients.map(c => {
                    const nom = c.nom || c.data?.['CLIENT NOM'] || '';
                    const prenom = c.prenom || c.data?.['CLIENT PRENOM'] || '';
                    const tel = c.telephone || c.data?.['CLIENT TELEPHONE'] || '';
                    const ville = c.ville || c.data?.['VILLE RDV'] || '';
                    return (
                      <button
                        key={c.row_number}
                        data-testid={`devis-client-${c.row_number}`}
                        onClick={() => { setSelectedClient(c); setStep(2); }}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          selectedClient?.row_number === c.row_number
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-slate-700 bg-slate-800/50 hover:bg-slate-700/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-400" />
                          </div>
                          <div>
                            <div className="text-white font-medium">{nom} {prenom}</div>
                            <div className="text-xs text-slate-400">{tel} {ville && `- ${ville}`}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {clientSearch.length >= 2 && clients.length === 0 && (
                <div className="text-center py-6 text-slate-500">Aucun client trouve</div>
              )}
            </div>
          )}

          {/* STEP 2: Select products */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Client summary */}
              <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                <User className="w-5 h-5 text-orange-400" />
                <div>
                  <span className="text-white font-medium">
                    {selectedClient?.nom || selectedClient?.data?.['CLIENT NOM']} {selectedClient?.prenom || selectedClient?.data?.['CLIENT PRENOM']}
                  </span>
                  <span className="text-slate-400 text-sm ml-3">{selectedClient?.telephone || selectedClient?.data?.['CLIENT TELEPHONE']}</span>
                </div>
                <button onClick={() => setStep(1)} className="ml-auto text-xs text-blue-400 hover:underline">Changer</button>
              </div>

              {/* AMO / MAR selection */}
              {type === 'mpa' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1.5 flex items-center gap-1"><Building2 className="w-3 h-3" /> AMO</label>
                    <select
                      data-testid="devis-select-amo"
                      value={selectedAmo?.id || ''}
                      onChange={e => setSelectedAmo(amoMarList.find(a => a.id === e.target.value) || null)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      <option value="">-- Selectionner l'AMO --</option>
                      {amoMarList.filter(a => a.type === 'AMO' && a.actif).map(a => (
                        <option key={a.id} value={a.id}>{a.nom_societe}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1.5 flex items-center gap-1"><Building2 className="w-3 h-3" /> MAR</label>
                    <select
                      data-testid="devis-select-mar"
                      value={selectedMar?.id || ''}
                      onChange={e => setSelectedMar(amoMarList.find(a => a.id === e.target.value) || null)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      <option value="">-- Selectionner le MAR --</option>
                      {amoMarList.filter(a => a.type === 'MAR' && a.actif).map(a => (
                        <option key={a.id} value={a.id}>{a.nom_societe}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Receveur selector */}
              {type === 'mpa' && receveurs.length > 0 && (
                <div className="bg-slate-800/60 border border-cyan-500/30 rounded-xl p-4">
                  <label className="block text-xs text-slate-400 mb-2 flex items-center gap-1.5">
                    <ShowerHead className="w-3.5 h-3.5 text-cyan-400" /> Choix du receveur de douche
                  </label>
                  <select
                    data-testid="devis-select-receveur"
                    value={selectedReceveur?.id || ''}
                    onChange={e => setSelectedReceveur(receveurs.find(r => r.id === e.target.value) || null)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white"
                  >
                    <option value="">-- Selectionner un receveur --</option>
                    {receveurs.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.marque} {r.taille} — {r.nom?.replace('Receveur ', '')} — {formatPrice(r.prix_ht)} HT
                      </option>
                    ))}
                  </select>
                  {selectedReceveur && (
                    <div className="mt-2 flex items-center justify-between p-2 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                      <div>
                        <div className="text-sm text-white font-medium">{selectedReceveur.nom}</div>
                        <div className="text-xs text-slate-400">
                          {selectedReceveur.taille} | {selectedReceveur.couleur || 'Blanc'} | Ref: {selectedReceveur.reference || '-'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-cyan-400 font-mono font-bold">{formatPrice(selectedReceveur.prix_ht)} HT</div>
                        <div className="text-xs text-slate-500 font-mono">{formatPrice(selectedReceveur.prix_ht * 1.1)} TTC</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Paroi selector */}
              {type === 'mpa' && (
                <div className="bg-slate-800/60 border border-amber-500/30 rounded-xl p-4">
                  <label className="block text-xs text-slate-400 mb-2 flex items-center gap-1.5">
                    <DoorOpen className="w-3.5 h-3.5 text-amber-400" /> Choix de la paroi de douche
                    {parois.length === 0 && <span className="text-red-400 text-xs ml-2">(chargement...)</span>}
                  </label>
                  <select
                    data-testid="devis-select-paroi"
                    value={selectedParoi?.id || ''}
                    onChange={e => setSelectedParoi(parois.find(p => p.id === e.target.value) || null)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white"
                  >
                    <option value="">-- Selectionner une paroi --</option>
                    {(() => {
                      const byType = {};
                      parois.forEach(p => {
                        const t = p.type_paroi || 'Autre';
                        if (!byType[t]) byType[t] = [];
                        byType[t].push(p);
                      });
                      return Object.entries(byType).map(([tp, items]) => (
                        <optgroup key={tp} label={tp}>
                          {items.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.marque} {p.taille} — {p.nom?.replace('Paroi ', '').replace(`${tp} `, '')} — {formatPrice(p.prix_ht)} HT
                            </option>
                          ))}
                        </optgroup>
                      ));
                    })()}
                  </select>
                  {selectedParoi && (
                    <div className="mt-2 flex items-center justify-between p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                      <div>
                        <div className="text-sm text-white font-medium">{selectedParoi.nom}</div>
                        <div className="text-xs text-slate-400">
                          {selectedParoi.type_paroi} | {selectedParoi.taille} | {selectedParoi.couleur || 'Chrome'} | Ref: {selectedParoi.reference || '-'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-amber-400 font-mono font-bold">{formatPrice(selectedParoi.prix_ht)} HT</div>
                        <div className="text-xs text-slate-500 font-mono">{formatPrice(selectedParoi.prix_ht * 1.1)} TTC</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Meuble selector */}
              {type === 'mpa' && (
                <div className="bg-slate-800/60 border border-violet-500/30 rounded-xl p-4">
                  <label className="block text-xs text-slate-400 mb-2 flex items-center gap-1.5">
                    <Bath className="w-3.5 h-3.5 text-violet-400" /> Choix de l'ensemble meuble lavabo
                    {meubles.length === 0 && <span className="text-red-400 text-xs ml-2">(aucun meuble importe)</span>}
                  </label>
                  <select
                    data-testid="devis-select-meuble"
                    value={selectedMeuble?.id || ''}
                    onChange={e => setSelectedMeuble(meubles.find(m => m.id === e.target.value) || null)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white"
                  >
                    <option value="">-- Selectionner un meuble --</option>
                    {(() => {
                      const byType = {};
                      meubles.forEach(m => {
                        const t = m.type_meuble || 'Autre';
                        if (!byType[t]) byType[t] = [];
                        byType[t].push(m);
                      });
                      return Object.entries(byType).map(([tp, items]) => (
                        <optgroup key={tp} label={tp}>
                          {items.map(m => (
                            <option key={m.id} value={m.id}>
                              {m.marque} {m.taille} {m.couleur && m.couleur !== 'N/A' ? `- ${m.couleur}` : ''} — {formatPrice(m.prix_ht)} HT
                            </option>
                          ))}
                        </optgroup>
                      ));
                    })()}
                  </select>
                  {selectedMeuble && (
                    <div className="mt-2 flex items-center justify-between p-2 bg-violet-500/10 border border-violet-500/20 rounded-lg">
                      <div>
                        <div className="text-sm text-white font-medium">{selectedMeuble.nom}</div>
                        <div className="text-xs text-slate-400">
                          {selectedMeuble.type_meuble} | {selectedMeuble.taille} | {selectedMeuble.couleur || '-'} | Ref: {selectedMeuble.reference || '-'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-violet-400 font-mono font-bold">{formatPrice(selectedMeuble.prix_ht)} HT</div>
                        <div className="text-xs text-slate-500 font-mono">{formatPrice(selectedMeuble.prix_ht * 1.1)} TTC</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Revetement surface fields */}
              {type === 'mpa' && (
                <div className="bg-slate-800/60 border border-emerald-500/30 rounded-xl p-4">
                  <label className="block text-xs text-slate-400 mb-3 font-semibold">Surfaces revêtements (m²)</label>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Mur */}
                    <div className="space-y-2">
                      <label className="text-xs text-emerald-400 font-medium">Panneaux muraux PVC</label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="text-[10px] text-slate-500">Superficie reelle (m²)</label>
                          <input
                            data-testid="devis-surface-mur"
                            type="number" step="0.1" min="0"
                            value={surfaceMur || ''}
                            onChange={e => setSurfaceMur(parseFloat(e.target.value) || 0)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                            placeholder="Ex: 12"
                          />
                        </div>
                        <div className="w-24">
                          <label className="text-[10px] text-slate-500">m²/boite</label>
                          <input
                            data-testid="devis-m2-boite-mur"
                            type="number" step="0.01" min="0.01"
                            value={m2ParBoiteMur}
                            onChange={e => setM2ParBoiteMur(parseFloat(e.target.value) || 1.95)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                          />
                        </div>
                      </div>
                      {surfaceMur > 0 && (
                        <div className="text-[11px] text-slate-400 bg-slate-900/50 rounded p-1.5">
                          {surfaceMur} m² + 15% = {(surfaceMur * 1.15).toFixed(2)} m² → <span className="text-emerald-400 font-bold">{Math.ceil(surfaceMur * 1.15 / m2ParBoiteMur)} boites</span>
                        </div>
                      )}
                    </div>
                    {/* Sol */}
                    <div className="space-y-2">
                      <label className="text-xs text-emerald-400 font-medium">Dalles sols antiderapantes</label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="text-[10px] text-slate-500">Superficie reelle (m²)</label>
                          <input
                            data-testid="devis-surface-sol"
                            type="number" step="0.1" min="0"
                            value={surfaceSol || ''}
                            onChange={e => setSurfaceSol(parseFloat(e.target.value) || 0)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                            placeholder="Ex: 6"
                          />
                        </div>
                        <div className="w-24">
                          <label className="text-[10px] text-slate-500">m²/boite</label>
                          <input
                            data-testid="devis-m2-boite-sol"
                            type="number" step="0.01" min="0.01"
                            value={m2ParBoiteSol}
                            onChange={e => setM2ParBoiteSol(parseFloat(e.target.value) || 1.95)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                          />
                        </div>
                      </div>
                      {surfaceSol > 0 && (
                        <div className="text-[11px] text-slate-400 bg-slate-900/50 rounded p-1.5">
                          {surfaceSol} m² + 15% = {(surfaceSol * 1.15).toFixed(2)} m² → <span className="text-emerald-400 font-bold">{Math.ceil(surfaceSol * 1.15 / m2ParBoiteSol)} boites</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Saved custom prestations dropdown */}
              {type === 'mpa' && savedPrestations.length > 0 && (
                <div className="bg-slate-800/60 border border-purple-500/20 rounded-xl p-4">
                  <label className="block text-xs text-slate-400 mb-2 flex items-center gap-1.5">
                    <Package className="w-3.5 h-3.5 text-purple-400" /> Prestations sauvegardees
                  </label>
                  <select
                    data-testid="devis-select-saved-prestation"
                    value=""
                    onChange={e => { if (e.target.value) addSavedPrestation(e.target.value); e.target.value = ''; }}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white"
                  >
                    <option value="">-- Ajouter une prestation sauvegardee --</option>
                    {savedPrestations.map(sp => (
                      <option key={sp.id} value={sp.id}>
                        {sp.nom} — {formatPrice(sp.prix_ht)} HT ({sp.unite})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Custom prestation via IA */}
              {type === 'mpa' && (
                <div className="bg-slate-800/60 border border-purple-500/30 rounded-xl p-4">
                  <label className="block text-xs text-slate-400 mb-2 flex items-center gap-1.5 font-semibold">
                    <Sparkles className="w-3.5 h-3.5 text-purple-400" /> Ajouter une prestation (reformulation IA — Elodie Rousseau)
                  </label>
                  <textarea
                    data-testid="devis-custom-desc"
                    value={customDesc}
                    onChange={e => setCustomDesc(e.target.value)}
                    placeholder="Decrivez la prestation a ajouter... Ex: installer une barre d'appui coudee dans la douche"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white resize-none"
                    rows={2}
                  />
                  <button
                    data-testid="devis-reformulate-btn"
                    onClick={handleReformulate}
                    disabled={!customDesc.trim() || customLoading}
                    className="mt-2 flex items-center gap-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-30 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    {customLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {customLoading ? 'Reformulation...' : 'Reformuler avec IA'}
                  </button>

                  {customResult && customResult.success !== false && (
                    <div className="mt-3 bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-sm text-white font-medium">{customResult.nom}</div>
                          <div className="text-xs text-slate-300 mt-1">{customResult.description}</div>
                          <div className="text-[10px] text-purple-400/70 mt-1 italic">{customResult.justification_prix}</div>
                          <div className="text-[10px] text-slate-500 mt-0.5">{customResult.auteur}</div>
                        </div>
                        <div className="text-right ml-3 flex-shrink-0">
                          <div className="text-xs text-slate-400 mb-1">Prix HT suggere</div>
                          <input
                            data-testid="devis-custom-prix"
                            type="number" step="0.01"
                            value={customResult.prix_ht}
                            onChange={e => setCustomResult(prev => ({ ...prev, prix_ht: parseFloat(e.target.value) || 0 }))}
                            className="w-24 bg-slate-900 border border-purple-500/40 rounded px-2 py-1 text-sm text-right text-purple-300 font-mono font-bold"
                          />
                          <div className="text-[10px] text-slate-500 mt-0.5">{customResult.unite}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          data-testid="devis-custom-validate"
                          onClick={addCustomPrestation}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-medium transition-colors"
                        >
                          <PlusCircle className="w-3.5 h-3.5" /> Valider et ajouter au devis
                        </button>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <button
                            data-testid="devis-custom-save-db"
                            onClick={() => setSaveToDb(p => !p)}
                            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                              saveToDb ? 'bg-purple-500 border-purple-500' : 'bg-slate-700 border-slate-500'
                            }`}
                          >
                            {saveToDb && <Check className="w-2.5 h-2.5 text-white" />}
                          </button>
                          <span className="text-[11px] text-slate-400">Sauvegarder pour reutilisation</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Sticky total bar */}
              <div className="sticky top-0 z-10 flex items-center justify-between p-3 bg-slate-800 border border-slate-700 rounded-lg">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-400"><Package className="w-4 h-4 inline mr-1" />{nbSelected} prestations</span>
                  <span className="text-sm text-white font-mono">Total HT: {formatPrice(totalHT)}</span>
                  <span className="text-sm text-slate-400 font-mono">TTC: {formatPrice(totalTTC)}</span>
                </div>
                <div className="flex items-center gap-2">
                  {checkedForDelete.size > 0 && (
                    <button
                      data-testid="devis-delete-selected-btn"
                      onClick={deleteChecked}
                      className="flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      Supprimer ({checkedForDelete.size})
                    </button>
                  )}
                  <button
                    data-testid="devis-step-resume"
                    onClick={() => setStep(3)}
                    disabled={nbSelected === 0}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-30 text-white rounded-lg text-sm font-medium"
                  >
                    Resume →
                  </button>
                </div>
              </div>

              {/* Products */}
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {Object.entries(grouped).sort(([,a],[,b]) => (a[0]?.section_order || 0) - (b[0]?.section_order || 0)).map(([section, items]) => (
                  <div key={section} className="bg-slate-800/30 border border-slate-700/30 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpandedSections(p => ({ ...p, [section]: !p[section] }))}
                      className="w-full flex items-center justify-between p-3 hover:bg-slate-700/20"
                    >
                      <span className="text-sm font-semibold text-orange-400">{section}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">
                          {items.filter(i => selectedItems[i.id]).length}/{items.length}
                        </span>
                        {expandedSections[section] ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>
                    {expandedSections[section] && (
                      <div className="border-t border-slate-700/30 divide-y divide-slate-700/20">
                        {items.sort((a, b) => a.order - b.order).map(item => {
                          const displayItem = getDisplayItem(item);
                          const replaced = isReplacedItem(item.id);
                          const replacement = getReplacementFor(item.id);
                          const isReceveur = item.id === genericReceveurId && selectedReceveur;
                          const isParoi = item.id === genericParoiId && selectedParoi;
                          const isMeuble = item.id === genericMeubleId && selectedMeuble;
                          const sel = selectedItems[item.id];
                          const qty = sel ? sel.qty : 0;
                          const prixDisplay = replaced ? replacement.prix_ht : (sel ? sel.prix_ht : item.prix_ht);
                          const colorClass = isReceveur ? 'text-cyan-400' : isParoi ? 'text-amber-400' : isMeuble ? 'text-violet-400' : 'text-white';
                          const isMarkedDelete = checkedForDelete.has(item.id);
                          const isRevetement = isRevetementItem(item.id);
                          const isAutoQty = isRevetement && ((item.id === panneauxMurauxId && surfaceMur > 0) || (item.id === dallesSolId && surfaceSol > 0) || (item.id === poseMuralId && surfaceMur > 0) || (item.id === poseSolId && surfaceSol > 0));
                          return (
                            <div key={item.id} className={`flex items-center gap-2 px-3 py-2 ${isMarkedDelete ? 'bg-red-500/10' : qty > 0 ? 'bg-orange-500/5' : 'hover:bg-slate-800/30'}`}>
                              {/* Delete checkbox - only for added items */}
                              {qty > 0 ? (
                                <button
                                  data-testid={`devis-delete-check-${item.id}`}
                                  onClick={() => toggleDeleteCheck(item.id)}
                                  className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                                    isMarkedDelete ? 'bg-red-500 border-red-500 shadow-[0_0_6px_rgba(239,68,68,0.4)]' : 'bg-slate-600 border-slate-400 hover:border-red-400 hover:bg-slate-500'
                                  }`}
                                >
                                  {isMarkedDelete && <X className="w-3 h-3 text-white" />}
                                </button>
                              ) : (
                                <div className="w-5 flex-shrink-0" />
                              )}
                              {/* Name - clickable to add */}
                              <div
                                className={`flex-1 min-w-0 ${qty === 0 ? 'cursor-pointer' : ''}`}
                                onClick={() => { if (qty === 0) toggleItem(item.id); }}
                              >
                                <div className={`text-sm ${qty > 0 ? colorClass : 'text-slate-400 hover:text-white'}`}>
                                  {isReceveur && <ShowerHead className="w-3.5 h-3.5 inline mr-1" />}
                                  {isParoi && <DoorOpen className="w-3.5 h-3.5 inline mr-1" />}
                                  {isMeuble && <Bath className="w-3.5 h-3.5 inline mr-1" />}
                                  {displayItem.nom}
                                  {replaced && <span className={`text-xs ${isReceveur ? 'text-cyan-500/60' : 'text-amber-500/60'} ml-1`}>({replacement.taille})</span>}
                                </div>
                                {displayItem.marque && <span className="text-xs text-slate-500">{displayItem.marque}</span>}
                              </div>
                              {/* Qty + Price for added items */}
                              {qty > 0 ? (
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {!replaced && !isAutoQty && (
                                    <div className="flex items-center gap-0.5 bg-slate-800 rounded border border-slate-700">
                                      <button
                                        data-testid={`devis-qty-minus-${item.id}`}
                                        onClick={() => updateItemQty(item.id, qty - 1)}
                                        className="p-1.5 hover:bg-slate-600 bg-slate-700 rounded-l border-r border-slate-600"
                                      ><Minus className="w-3 h-3 text-white" /></button>
                                      <span className="text-xs w-7 text-center font-mono text-orange-300 font-bold bg-slate-750">{qty}</span>
                                      <button
                                        data-testid={`devis-qty-plus-${item.id}`}
                                        onClick={() => updateItemQty(item.id, qty + 1)}
                                        className="p-1.5 hover:bg-slate-600 bg-slate-700 rounded-r border-l border-slate-600"
                                      ><Plus className="w-3 h-3 text-white" /></button>
                                    </div>
                                  )}
                                  {isAutoQty && (
                                    <div className="flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1">
                                      <span className="text-xs font-mono text-emerald-400 font-bold">{qty}</span>
                                      <span className="text-[10px] text-emerald-500/70">
                                        {(item.id === panneauxMurauxId || item.id === dallesSolId) ? 'boites' : 'm²'}
                                      </span>
                                    </div>
                                  )}
                                  {!replaced ? (
                                    <input
                                      data-testid={`devis-price-${item.id}`}
                                      type="number"
                                      step="0.01"
                                      value={sel.prix_ht}
                                      onChange={e => updateItemPrice(item.id, parseFloat(e.target.value) || 0)}
                                      className="w-20 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-right text-white font-mono"
                                    />
                                  ) : (
                                    <span className="text-xs font-mono w-20 text-right text-slate-400">{formatPrice(prixDisplay)}</span>
                                  )}
                                  <span className="text-xs text-slate-500 w-8">{item.unite}</span>
                                  <span className={`text-xs font-mono font-bold w-20 text-right ${replaced ? colorClass : 'text-orange-400'}`}>
                                    {formatPrice(qty * prixDisplay)}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-xs text-slate-500 font-mono">{formatPrice(item.prix_ht)}/{item.unite}</span>
                                  <button
                                    data-testid={`devis-add-${item.id}`}
                                    onClick={() => toggleItem(item.id)}
                                    className="p-1.5 bg-orange-600/80 hover:bg-orange-500 border border-orange-400/50 rounded transition-colors"
                                  ><Plus className="w-3.5 h-3.5 text-white" /></button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}

                {/* Custom prestations section */}
                {customPrestations.length > 0 && (
                  <div className="bg-slate-800/30 border border-purple-500/30 rounded-xl overflow-hidden">
                    <div className="w-full flex items-center justify-between p-3">
                      <span className="text-sm font-semibold text-purple-400">Autres prestations</span>
                      <span className="text-xs text-slate-500">{customPrestations.length}</span>
                    </div>
                    <div className="border-t border-purple-500/20 divide-y divide-slate-700/20">
                      {customPrestations.map(cp => {
                        const sel = selectedItems[cp.id];
                        const qty = sel?.qty || 1;
                        const isMarkedDelete = checkedForDelete.has(cp.id);
                        return (
                          <div key={cp.id} className={`flex items-center gap-2 px-3 py-2 ${isMarkedDelete ? 'bg-red-500/10' : 'bg-purple-500/5'}`}>
                            <button
                              data-testid={`devis-delete-check-${cp.id}`}
                              onClick={() => toggleDeleteCheck(cp.id)}
                              className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                                isMarkedDelete ? 'bg-red-500 border-red-500' : 'bg-slate-600 border-slate-400 hover:border-red-400'
                              }`}
                            >
                              {isMarkedDelete && <X className="w-3 h-3 text-white" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-purple-300 flex items-center gap-1">
                                <Sparkles className="w-3 h-3" /> {cp.nom}
                              </div>
                              <div className="text-xs text-slate-500 truncate">{cp.description}</div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <div className="flex items-center gap-0.5 bg-slate-800 rounded border border-slate-700">
                                <button onClick={() => updateItemQty(cp.id, qty - 1)} className="p-1.5 hover:bg-slate-600 bg-slate-700 rounded-l border-r border-slate-600"><Minus className="w-3 h-3 text-white" /></button>
                                <span className="text-xs w-7 text-center font-mono text-purple-300 font-bold">{qty}</span>
                                <button onClick={() => updateItemQty(cp.id, qty + 1)} className="p-1.5 hover:bg-slate-600 bg-slate-700 rounded-r border-l border-slate-600"><Plus className="w-3 h-3 text-white" /></button>
                              </div>
                              <input
                                type="number" step="0.01"
                                value={sel?.prix_ht || 0}
                                onChange={e => updateItemPrice(cp.id, parseFloat(e.target.value) || 0)}
                                className="w-20 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-right text-white font-mono"
                              />
                              <span className="text-xs text-slate-500 w-10">{cp.unite}</span>
                              <span className="text-xs text-purple-400 font-mono w-20 text-right font-bold">{formatPrice(qty * (sel?.prix_ht || 0))}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 3: Resume */}
          {step === 3 && (
            <div className="space-y-5">
              <button onClick={() => setStep(2)} className="text-sm text-blue-400 hover:underline">← Retour aux prestations</button>

              {/* Client */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                <h3 className="text-sm font-semibold text-slate-400 mb-2 flex items-center gap-2"><User className="w-4 h-4" /> Client</h3>
                <div className="text-white font-medium">
                  {selectedClient?.nom || selectedClient?.data?.['CLIENT NOM']} {selectedClient?.prenom || selectedClient?.data?.['CLIENT PRENOM']}
                </div>
                <div className="text-sm text-slate-400">
                  {selectedClient?.telephone || selectedClient?.data?.['CLIENT TELEPHONE']}
                  {(selectedClient?.adresse || selectedClient?.data?.['ADRESSE RDV']) && ` - ${selectedClient?.adresse || selectedClient?.data?.['ADRESSE RDV']}`}
                  {(selectedClient?.ville || selectedClient?.data?.['VILLE RDV']) && `, ${selectedClient?.code_postal || selectedClient?.data?.['CODE POSTAL RDV']} ${selectedClient?.ville || selectedClient?.data?.['VILLE RDV']}`}
                </div>
              </div>

              {/* AMO / MAR */}
              {type === 'mpa' && (selectedAmo || selectedMar) && (
                <div className="grid grid-cols-2 gap-3">
                  {selectedAmo && (
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-emerald-500/30">
                      <h3 className="text-xs font-semibold text-emerald-400 mb-1 flex items-center gap-1"><Building2 className="w-3 h-3" /> AMO</h3>
                      <div className="text-white text-sm font-medium">{selectedAmo.nom_societe}</div>
                      {selectedAmo.dirigeant && <div className="text-xs text-slate-400">{selectedAmo.dirigeant}</div>}
                    </div>
                  )}
                  {selectedMar && (
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-blue-500/30">
                      <h3 className="text-xs font-semibold text-blue-400 mb-1 flex items-center gap-1"><Building2 className="w-3 h-3" /> MAR</h3>
                      <div className="text-white text-sm font-medium">{selectedMar.nom_societe}</div>
                      {selectedMar.dirigeant && <div className="text-xs text-slate-400">{selectedMar.dirigeant}</div>}
                    </div>
                  )}
                </div>
              )}

              {/* Receveur selection summary */}
              {type === 'mpa' && selectedReceveur && (
                <div className="bg-slate-800/50 rounded-xl p-4 border border-cyan-500/30">
                  <h3 className="text-xs font-semibold text-cyan-400 mb-1 flex items-center gap-1"><ShowerHead className="w-3 h-3" /> Receveur de douche</h3>
                  <div className="text-white text-sm font-medium">{selectedReceveur.nom}</div>
                  <div className="text-xs text-slate-400">{selectedReceveur.taille} | {selectedReceveur.couleur || 'Blanc'} | Ref: {selectedReceveur.reference || '-'} | {formatPrice(selectedReceveur.prix_ht)} HT</div>
                </div>
              )}

              {/* Paroi selection summary */}
              {type === 'mpa' && selectedParoi && (
                <div className="bg-slate-800/50 rounded-xl p-4 border border-amber-500/30">
                  <h3 className="text-xs font-semibold text-amber-400 mb-1 flex items-center gap-1"><DoorOpen className="w-3 h-3" /> Paroi de douche</h3>
                  <div className="text-white text-sm font-medium">{selectedParoi.nom}</div>
                  <div className="text-xs text-slate-400">{selectedParoi.type_paroi} | {selectedParoi.taille} | {selectedParoi.couleur || 'Chrome'} | Ref: {selectedParoi.reference || '-'} | {formatPrice(selectedParoi.prix_ht)} HT</div>
                </div>
              )}

              {/* Meuble selection summary */}
              {type === 'mpa' && selectedMeuble && (
                <div className="bg-slate-800/50 rounded-xl p-4 border border-violet-500/30">
                  <h3 className="text-xs font-semibold text-violet-400 mb-1 flex items-center gap-1"><Bath className="w-3 h-3" /> Ensemble meuble lavabo</h3>
                  <div className="text-white text-sm font-medium">{selectedMeuble.nom}</div>
                  <div className="text-xs text-slate-400">{selectedMeuble.type_meuble} | {selectedMeuble.taille} | {selectedMeuble.couleur || '-'} | Ref: {selectedMeuble.reference || '-'} | {formatPrice(selectedMeuble.prix_ht)} HT</div>
                </div>
              )}

              {/* Custom prestations summary */}
              {customPrestations.length > 0 && (
                <div className="bg-slate-800/50 rounded-xl p-4 border border-purple-500/30">
                  <h3 className="text-xs font-semibold text-purple-400 mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Autres prestations ({customPrestations.length})</h3>
                  {customPrestations.map(cp => {
                    const sel = selectedItems[cp.id];
                    return (
                      <div key={cp.id} className="flex items-center justify-between text-sm py-1 border-b border-purple-500/20">
                        <span className="text-purple-300">{cp.nom}</span>
                        <span className="font-mono text-purple-300">{sel?.qty || 1} x {formatPrice(sel?.prix_ht || cp.prix_ht)} = {formatPrice((sel?.qty || 1) * (sel?.prix_ht || cp.prix_ht))}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* MPA info */}
              {type === "mpa" && (
                <div className="bg-slate-800/50 rounded-xl p-4 border border-violet-500/30">
                  <h3 className="text-sm font-semibold text-violet-400 mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Informations MaPrimeAdapt'</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-400">Condition GIR</label>
                      <select
                        value={devisInfo.condition_gir}
                        onChange={e => setDevisInfo(d => ({ ...d, condition_gir: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white mt-1"
                      >
                        <option value="Sans Condition de GIR">Sans Condition de GIR</option>
                        <option value="GIR 1 a 4">GIR 1 a 4</option>
                        <option value="GIR 5 a 6">GIR 5 a 6</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">Estimation MaPrimeAdapt' / ANAH</label>
                      <input
                        data-testid="devis-estimation-mpa"
                        type="number"
                        step="100"
                        value={devisInfo.estimation_mpa}
                        onChange={e => setDevisInfo(d => ({ ...d, estimation_mpa: parseFloat(e.target.value) || 0 }))}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white mt-1"
                        placeholder="Ex: 15400"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Lines summary */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                <h3 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2"><Package className="w-4 h-4" /> Prestations ({nbSelected})</h3>
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {Object.entries(selectedItems).map(([id, { qty, prix_ht }]) => {
                    const item = catalogue.find(i => i.id === id);
                    if (!item) return null;
                    const replaced = isReplacedItem(id);
                    const replacement = getReplacementFor(id);
                    const isReceveur = id === genericReceveurId && selectedReceveur;
                    const isParoi = id === genericParoiId && selectedParoi;
                    const isMeuble = id === genericMeubleId && selectedMeuble;
                    const nomDisplay = replaced ? replacement.nom : item.nom;
                    const prixDisplay = replaced ? replacement.prix_ht : prix_ht;
                    const colorClass = isReceveur ? 'text-cyan-400' : isParoi ? 'text-amber-400' : isMeuble ? 'text-violet-400' : 'text-white';
                    const borderClass = replaced ? (isReceveur ? 'border-cyan-500/30' : isParoi ? 'border-amber-500/30' : 'border-violet-500/30') : 'border-slate-700/20';
                    return (
                      <div key={id} className={`flex items-center justify-between text-sm py-1 border-b ${borderClass}`}>
                        <span className={`${colorClass} ${replaced ? 'flex items-center gap-1.5' : ''}`}>
                          {isReceveur && <ShowerHead className="w-3.5 h-3.5" />}
                          {isParoi && <DoorOpen className="w-3.5 h-3.5" />}
                          {isMeuble && <Bath className="w-3.5 h-3.5" />}
                          {nomDisplay}
                        </span>
                        <span className={`font-mono ${replaced ? colorClass : 'text-slate-400'}`}>
                          {qty} x {formatPrice(prixDisplay)} = {formatPrice(qty * prixDisplay)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs text-slate-400">Notes / Observations</label>
                <textarea
                  value={devisInfo.notes}
                  onChange={e => setDevisInfo(d => ({ ...d, notes: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white mt-1 h-20"
                  placeholder="Notes optionnelles..."
                />
              </div>

              {/* Totals */}
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 border border-orange-500/30">
                <h3 className="text-sm font-semibold text-orange-400 mb-3 flex items-center gap-2"><Calculator className="w-4 h-4" /> Recapitulatif</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Total HT</span>
                    <span className="text-white font-mono font-bold">{formatPrice(totalHT)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">TVA {tvaRate}%</span>
                    <span className="text-slate-300 font-mono">{formatPrice(totalTVA)}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-slate-700 pt-2">
                    <span className="text-white font-semibold">Total TTC</span>
                    <span className="text-white font-mono font-bold text-lg">{formatPrice(totalTTC)}</span>
                  </div>
                  {type === "mpa" && devisInfo.estimation_mpa > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-violet-400">
                        <span>Estimation MaPrimeAdapt'</span>
                        <span className="font-mono">- {formatPrice(devisInfo.estimation_mpa)}</span>
                      </div>
                      <div className="flex justify-between text-sm border-t border-orange-500/30 pt-2">
                        <span className="text-orange-400 font-bold">Reste a payer</span>
                        <span className="text-orange-400 font-mono font-bold text-lg">{formatPrice(resteAPayer)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-slate-700">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white text-sm">Annuler</button>
          <div className="flex gap-3">
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm">Precedent</button>
            )}
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 ? !selectedClient : nbSelected === 0}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-30 text-white rounded-lg text-sm font-medium"
              >
                Suivant →
              </button>
            ) : (
              <button
                data-testid="devis-save-btn"
                onClick={handleSave}
                disabled={saving || nbSelected === 0}
                className="flex items-center gap-2 px-6 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-30 text-white rounded-lg text-sm font-medium"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Creer le devis
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
